import { client } from '../client';
import { APIRequest } from '../client/Request';
import { IconTransferType } from '~/elements';
import { PlatformRolePermissionKey } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { TransferServiceListTransfersParams, UnifiedTransfer, UnifiedTransfers } from '~/typings/API';
import { convertValues } from '~/util/convertValues';

export type TransferFilterParams = TransferServiceListTransfersParams;
export type TransferListResponse = UnifiedTransfers;

export type TransferEntry = UnifiedTransfer;
export type TransferTypeID =
  | 'ach'
  | 'book'
  | 'issue-check'
  | 'wire'
  | 'wire-drawdown'
  | 'rdc'
  | 'check'
  | 'intl-wire'
  | 'fi-transfer'
  | 'realtime'
  | 'ach-batch';
export enum TransferEntryTypes {
  Ach = 'ach',
  AchBatch = 'ach_batch',
  AchCredit = 'ach_credit',
  AchDebit = 'ach_debit',
  Wire = 'wire',
  WireDrawdown = 'wire-drawdown',
  Book = 'book',
  CheckBatch = 'check_batch',
  CheckCredit = 'check_credit',
  CheckDebit = 'check_debit',
  Swift = 'swift',
  Realtime = 'realtime',
  Admin = 'admin',
  Crypto = 'crypto',
}
export type TransferEntryType = `${TransferEntryTypes}`;

type TransferAmountHandle = 1 | -1;
type TransferAmountKey = `${TransferEntryType}:${boolean}`;

const TransferAmountMap: Map<TransferAmountKey, TransferAmountHandle> = new Map([
  ['ach_batch:true', 1],
  ['ach_batch:false', 1],
  ['ach_credit:true', 1],
  ['ach_credit:false', -1],
  ['ach_debit:true', -1],
  ['ach_debit:false', 1],
  ['wire:true', 1],
  ['wire:false', -1],
  ['wire-drawdown:true', 1],
  ['wire-drawdown:false', -1],
  ['swift:true', 1],
  ['swift:false', -1],
  ['book:true', 1],
  ['book:false', -1],
  ['check_batch:true', 1],
  ['check_batch:false', 1],
  ['check_credit:true', 1],
  ['check_credit:false', 1],
  ['check_debit:true', -1],
  ['check_debit:false', -1],
  ['realtime:true', 1],
  ['realtime:false', -1],
  ['crypto:true', 1],
  ['crypto:false', -1],
  ['admin:true', 1],
  ['admin:false', -1],
]);

export const convertTransferAmount = (transferType: TransferEntryType, isIncoming: boolean): TransferAmountHandle => {
  return TransferAmountMap.get(`${transferType}:${isIncoming}` as TransferAmountKey) || 1;
};

export interface TransferType {
  id: TransferTypeID;
  idString?: string;
  icon: IconTransferType;
  label: string;
  isInstant?: boolean;
  isComingSoon?: boolean;
  permissionName?: PlatformRolePermissionKey[];
}

export const transfers: TransferType[] = [
  {
    id: 'ach',
    idString: 'acht_',
    icon: 'Ach',
    label: 'ACH Transfer',
    permissionName: ['achCredits', 'achDebits'],
  },
  {
    id: 'book',
    idString: 'book_',
    icon: 'Book',
    label: 'Book Transfer',
    isInstant: true,
    permissionName: ['bookTransfers'],
  },
  {
    id: 'wire',
    idString: 'wire_',
    icon: 'Wire',
    label: 'Wire Transfer',
    isInstant: true,
    permissionName: ['wireTransfers'],
  },
  {
    id: 'issue-check',
    icon: 'IssueCheck',
    label: 'Issue Check',
    permissionName: ['issueChecks'],
  },
  {
    id: 'rdc',
    icon: 'RDC',
    label: 'Remote Deposit Capture',
    permissionName: ['depositChecks'],
  },
  {
    id: 'check',
    idString: 'chkt_',
    icon: 'CheckScanner',
    label: 'Check Scanner',
    permissionName: ['depositChecks'],
  },
  {
    id: 'intl-wire',
    idString: 'swft_',
    icon: 'IntlWire',
    label: 'International Wire',
    permissionName: ['wireTransfers'],
    isComingSoon: true,
  },
  {
    id: 'fi-transfer',
    icon: 'Bank',
    label: 'International Treasury Wire',
    permissionName: ['wireTransfers'],
  },
  {
    id: 'realtime',
    icon: 'Realtime',
    label: 'Realtime',
    isInstant: true,
    permissionName: ['realtimeTransfers'],
  },
  {
    id: 'ach-batch',
    icon: 'Ach',
    label: 'NACHA Upload',
    permissionName: ['achCredits', 'achDebits'],
  },
];

export const TransferStatusObject = {
  ach: [
    'pre_review',
    'initiated',
    'pending_submission',
    'submitted',
    'settled',
    'returned',
    'completed',
    'canceled',
    'scheduled',
    'pending_return',
    'return_dishonored',
    'return_contested',
    'manual_review',
    'manual_review_approved',
  ],
  book: ['rejected', 'completed', 'hold', 'canceled'],
  wire: ['pre_review', 'initiated', 'pending_submission', 'submitted', 'completed', 'rejected', 'manual_review'],
  swift: [
    'initiated',
    'pending_submission',
    'submitted',
    'completed',
    'pending_return',
    'returned',
    'manual_review',
    'failed',
  ],
  checks: [
    'issued',
    'deposited',
    'pending_deposit',
    'stopped',
    'rejected',
    'pending_first_return',
    'pending_second_return',
    'first_return',
    'recleared',
    'pending_reclear',
    'second_return',
    'settled',
    'pending_user_initiated_return',
    'user_initiated_returned',
    'manual_review',
  ],
} as const;

export const TransferTypeOptions = [
  'ach_credit',
  'ach_debit',
  'wire',
  'book',
  'check_credit',
  'check_debit',
  'swift',
  'realtime',
  'crypto',
  'admin',
] as const;

export const TransferBatchTypeOptions = ['ach_batch', 'check_batch'] as const;

export const isBatchTypeValue = (type: unknown): boolean =>
  (TransferBatchTypeOptions as readonly string[]).includes(String(type));

const withBatchParams = (query?: Partial<TransferFilterParams>): Partial<TransferFilterParams> | undefined =>
  String(query?.type ?? '')
    .split(',')
    .some(isBatchTypeValue)
    ? ({ ...query, showBatches: true } as Partial<TransferFilterParams>)
    : query;

export class TransferRepository {
  static async getAll(query?: Partial<TransferFilterParams>) {
    return client
      .get<Partial<TransferFilterParams>, TransferListResponse>('/transfers', { query: withBatchParams(query) })
      .then((response) => ({
        transfers: Array.isArray(response?.transfers)
          ? response.transfers.map((e) => convertValues<UnifiedTransfer>(e))
          : [],
        hasMore: Boolean(response?.hasMore),
        totalResultsCount: response?.totalResultsCount ?? 0,
      }));
  }

  static async export(query?: Partial<TransferFilterParams>) {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => controller.abort(), 1800000);

    const request = new APIRequest<undefined, Partial<TransferFilterParams>>(
      '/transfers/export',
      'GET',
      undefined,
      withBatchParams(query),
      {
        'Content-Type': 'application/json',
        'Live-Mode': useSessionStore.getState().isSandbox ? 'No' : 'Yes',
        'Platform-Id': useSessionStore.getState().currentUser?.defaultPlatformId ?? '',
      },
      {
        signal: controller.signal,
      }
    );

    return fetch(request.getRequest())
      .then((response) => {
        clearTimeout(timeoutId);

        return response.text();
      })
      .catch((response) => {
        clearTimeout(timeoutId);

        return response;
      });
  }
}
